import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDocumentTitle } from '../../hooks/use-document-title';
import toast from '../../../utils/toast';
import styled from '@emotion/styled';
import { AxiosError } from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  ContentAndButtonsContainer,
  NavbarAndContentContainer,
} from '../../layout/layout-container.styled';
import { listItemMotion, m, Presence, Staleable, StateSwitch } from '../../../motion';
import { ListSkeleton } from '../dashboard/dashboard-shared-components/load-state';
import { ScreenHeader } from '../../layout/page-header';
import { NavButton } from '../../nav-button/nav-button';
import { DefaultComponentProps } from '../../app-router/component-props';
import { PersonField } from '../../capture/person-field';
import { UnitSelect } from '../../capture/unit-select';
import { ScanIdButton } from '../../capture/scan-id-button';
import {
  Package,
  listPackages,
  logPackage,
  releasePackage,
} from '../../../api/packages-client';
import {
  AmbiguityError,
  ResidentCandidate,
  ResolutionState,
} from '../../../api/residents-client';
import {
  layout,
  palette,
  radius,
  shadow,
  space,
  spacing,
  status as statusTone,
  transition,
  typography,
} from '../../../style-utils/theme';
import { down } from '../../../style-utils/media-queries';
// THE DESK'S OUT LANGUAGE, shared with "Coming out" and "Activities out". The
// shelf is the parcel half of the same act — a list of things still here, and
// one confirmed press per row — so it reads the same and is styled by the same
// rules. A local copy of a row and a tile is how three screens that do one job
// end up looking like three products.
import {
  RowAction,
  RowLead,
  RowMark,
  Stat as ShelfStat,
  Stats as ShelfStats,
} from '../visitor-exit/visitor-exit-page.styled';
import { initialsOf } from '../visitor-exit/desk-list';

// ---------------------------------------------------------------------------
// The parcel desk.
// ---------------------------------------------------------------------------
//
// The owner's own example for this whole build: "a package should give me a
// dropdown in case 2 tenants are named the same."
//
// So the one thing this screen must never do is let a parcel be handed over to
// a name rather than a person. The recipient is a resolved occupancy; the
// release button is disabled until it is; and if the desk gets past both of
// those, the database still refuses (chk_pkg_pickup_recipient).
//
// Most of the time none of that is visible: choosing a unit with one resident
// resolves the recipient with no interaction at all.
//
// ============ ONE SCREEN, TWO DOORS ============
//
// The main menu files intake under "Going in" and handover under "Coming out",
// which are two menu entries for what is physically one shelf. They are still
// ONE SCREEN: splitting it would have duplicated the shelf, the resident
// resolution and the release rules, and the day the two copies disagreed about
// `can_release` a parcel would go to the wrong Maria — the exact failure this
// screen exists to prevent.
//
// So `?section=` decides which half the desk LANDS on. Both halves always
// render: a concierge who came to hand a parcel over and is then handed one by
// a courier must not have to go back to the menu.

/* THE PAGE COLUMN. No padding of its own — the host card already pads, and this
   page was paying for it three times over (ContentAndButtonsContainer, then
   ContentWrapper, then here). Capped for the same reason the exit screens are:
   a row of "who it is for" and "release it" stops being one glance somewhere
   around 900px, and this page stretched to whatever the monitor was. */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  max-width: 880px;
  min-width: 0;
  min-height: 0;
  margin: 0 auto;
  overflow-y: auto;
`;

const Card = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.md};
`;

const CardTitle = styled.h2`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.sm};

  ${down('md')} {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

const Label = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  transition: ${transition.colors};

  &::placeholder {
    color: ${palette.slateLight};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

/* THE ROW WRAPS; THE TEXT NEVER SQUEEZES (2026-09-24).
   It was a nowrap row whose actions (pill + Release, about 230px) could not
   shrink, so on a phone the only thing left to give way was the text. At 390
   the name column measured 0px wide and every name and carrier was printed
   one letter per line - short names too, not only long ones. The exit
   screens' shared Row stacks under sm; that is a viewport answer, and this
   row's width depends on the card it sits in, so it wraps on its own width
   instead: the lead keeps SHELF_LEAD_MIN, and when the actions do not fit
   beside it they drop to a line of their own and wrap there if they must. */
const SHELF_LEAD_MIN = space(25);

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  transition: ${transition.base};

  & + & {
    margin-top: ${spacing.xs};
  }
  &:hover {
    box-shadow: ${shadow.sm};
  }
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

const RowName = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const RowMeta = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

const Pill = styled.span<{ tone: 'brand' | 'warning' | 'success' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  background: ${p => statusTone[p.tone].bg};
  color: ${p => statusTone[p.tone].fg};
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  align-items: center;
  flex: 0 1 auto;

  /* Under sm the row has always wrapped by here and RowAction is full width,
     so the group takes the whole line and the button spans the row. */
  ${down('sm')} {
    flex-grow: 1;
  }
`;

/* The mark and the two lines of text, with the floor the row wraps at. */
const ShelfLead = styled(RowLead)`
  flex: 1 1 ${SHELF_LEAD_MIN};
`;

const Alert = styled.p`
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${statusTone.alert.bg};
  color: ${statusTone.alert.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
`;

const Hint = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const ScanRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing.sm};
`;

const Empty = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

/* ONE PARCEL ON THE SHELF — the row and, while it is being handed over, its
   release form, as one motion element: it arrives in the cascade and, inside
   the Presence, leaves as one thing when the parcel is released. The sibling
   gap Row used to carry lives here, since Rows are no longer siblings. */
const ParcelItem = m.div`
  & + & {
    margin-top: ${spacing.xs};
  }
`;

/* Layout animation (survivors sliding up when one leaves) is for a shelf you
   can read at a glance; past this many rows the reflow costs more than it
   says, so the rows still fade but stop measuring each other. */
const LAYOUT_CAP = 60;

/* A HANDOVER ACROSS UNITS IS NORMAL, NOT AN ERROR. A neighbour collecting is
   legitimate and already common in the data, so this is a neutral statement of
   fact and never blocks Confirm. It exists because nothing else on this screen
   compares the collector's unit with the one the parcel is addressed to, and
   "wait, that is not their parcel" is a thing the desk should get the chance to
   notice while the person is still standing there. */
const CrossUnit = styled.p`
  margin: ${spacing.xs} 0 0;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${statusTone.neutral.bg};
  color: ${statusTone.neutral.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
`;

interface PersonDraft {
  name: string;
  occupancyId: string | null;
  resolution: ResolutionState;
  // The chosen resident's own unit, when one was chosen. Only the handover uses
  // it — see CrossUnit.
  unitLabel: string | null;
}

const EMPTY_PERSON: PersonDraft = {
  name: '',
  occupancyId: null,
  resolution: 'unset',
  unitLabel: null,
};

/** Which half of the desk a menu tile asked for. Anything else = the default. */
export type ParcelSection = 'intake' | 'handover';

export const resolveParcelSection = (
  raw: string | null
): ParcelSection | null => (raw === 'intake' || raw === 'handover' ? raw : null);



const ShelfSearchRow = styled.div`
  display: flex;
  margin-bottom: ${spacing.sm};
`;

const ShelfSearchInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;

  &::placeholder {
    color: ${palette.slateLight};
  }

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

/**
 * "Today", "3 days", "3 weeks" — the age of a parcel on the shelf.
 *
 * Deliberately coarse. The desk's question is never "at what minute did this
 * arrive"; it is "has this been here too long", and a precise timestamp makes
 * that arithmetic the reader's problem on every row.
 */
const waitingLabel = (loggedAt?: string | null): string => {
  if (!loggedAt) return '';

  const at = new Date(loggedAt);

  if (Number.isNaN(+at)) return '';

  const days = Math.floor((Date.now() - at.getTime()) / 86400000);

  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days`;
  if (days < 14) return '1 week';
  if (days < 60) return `${Math.floor(days / 7)} weeks`;

  return `${Math.floor(days / 30)} months`;
};

// The rail, the building panel and <main> come from DeskShell now
// (layout/desk-shell.tsx); this screen renders only what goes inside the card.
export const ParcelDesk: React.FC<DefaultComponentProps> = ({
  selectedBuilding,
}) => {
  useDocumentTitle('Parcels');

  const buildingId = Number(selectedBuilding?.id) || null;
  const [searchParams] = useSearchParams();
  // Read ONCE, on the way in. Re-reading would re-scroll the page under a desk
  // that had scrolled away, and the parameter is a starting point rather than a
  // mode: nothing on this screen is hidden by it.
  const section = resolveParcelSection(searchParams.get('section'));
  const handoverRef = useRef<HTMLElement | null>(null);

  const [packages, setPackages] = useState<Package[]>([]);
  /* SEARCH. The shelf rendered every awaiting parcel as one un-searchable,
     un-sorted list — and a front desk in a busy building can hold hundreds. The
     desk's question is always about ONE person, and the only way to answer it
     was to scroll. */
  const [shelfQuery, setShelfQuery] = useState('');
  const [loading, setLoading] = useState(false);
  // FIRST READ vs RE-READ. The shelf shows its skeleton once, before anything
  // has answered; every later refresh keeps the rows on screen and dims them.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* One flag for both writes: the desk cannot be taking a parcel in and handing
     one over at the same moment, and a single flag cannot leave one control
     stuck enabled while the other is busy. */
  const [submitting, setSubmitting] = useState(false);

  // Intake form.
  const [recipient, setRecipient] = useState<PersonDraft>(EMPTY_PERSON);
  const [unitLabel, setUnitLabel] = useState('');
  const [unitId, setUnitId] = useState<number | null>(null);
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [shelf, setShelf] = useState('');
  const [mustPick, setMustPick] = useState(false);
  // Candidates the SERVER refused over, so a rejected save reopens the picker
  // rather than making the desk search again.
  const [serverCandidates, setServerCandidates] = useState<ResidentCandidate[]>([]);
  // Provenance from a scan, sent with the intake so the recorded parcel knows
  // the name came off a document and what the scanner originally read.
  const [scanProvenance, setScanProvenance] = useState<
    Record<string, { source: 'scan'; raw: string; confidence: number }>
  >({});

  // Release form, keyed by the parcel being handed over.
  const [releasing, setReleasing] = useState<string | null>(null);
  const [collector, setCollector] = useState<PersonDraft>(EMPTY_PERSON);
  const [collectorMustPick, setCollectorMustPick] = useState(false);

  const refresh = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const res = await listPackages(buildingId, { status: 'awaiting_pickup' });
      setPackages(res.packages);
      setError(null);
    } catch {
      setError('Could not load the parcel shelf.');
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [buildingId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // HOW MANY OF THESE CANNOT LEAVE THE SHELF YET. `can_release` is the server's
  // own answer (the recipient has to be a resolved occupancy before a handover
  // is allowed at all — chk_pkg_pickup_recipient enforces it below this), so
  // the tile counts the same thing that disables the button rather than
  // re-deriving the rule from the resolution field beside it.
  const blockedCount = packages.filter(p => !p.can_release).length;

  // ONE WORD FOR WHAT THE SHELF IS, keying the crossfade below so a skeleton,
  // an empty shelf, a missed search and the rows replace each other in place.
  const shelfState = !hasLoaded
    ? 'loading'
    : packages.length === 0
      ? 'empty'
      : 'ready';

  /* Client-side: the shelf is already loaded whole, so a round trip per
     keystroke would add latency without adding an answer. The haystack is every
     field the row actually prints, so anything the desk can SEE is something
     they can search for. */
  const visibleParcels = useMemo(() => {
    const needle = shelfQuery.trim().toLowerCase();

    if (!needle) return packages;

    return packages.filter(parcel =>
      [
        parcel.recipient_name,
        parcel.unit_label,
        parcel.carrier,
        parcel.shelf,
        parcel.tracking_last4,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [packages, shelfQuery]);

  // THE SCROLL-TO-SHELF EFFECT IS GONE, AND SO IS THE PROBLEM IT PATCHED.
  //
  // The handover door used to open on the intake form with the shelf below it,
  // so an effect scrolled the shelf into view once the list had loaded. The
  // door now ORDERS the two halves instead (see the `order` on the two Cards),
  // which means the shelf is already the first thing under the header — and
  // scrolling it to `block: 'start'` from there would push the header off the
  // top, which is the opposite of arriving somewhere.
  //
  // Deleted rather than left as a no-op: an effect that does nothing, and the
  // ref that tracked whether it had fired, are two things a future reader has
  // to work out are dead. `handoverRef` stays — it is what the ordering is
  // anchored to and what a deep link would target.

  const handleRecipientPick = useCallback((v: boolean) => setMustPick(v), []);
  const handleCollectorPick = useCallback(
    (v: boolean) => setCollectorMustPick(v),
    []
  );

  const resetIntake = () => {
    setRecipient(EMPTY_PERSON);
    setUnitLabel('');
    setUnitId(null);
    setCarrier('');
    setTracking('');
    setShelf('');
    setServerCandidates([]);
    setScanProvenance({});
  };

  /**
   * NEITHER WRITE ON THIS SCREEN HAD AN IN-FLIGHT STATE OR A SUCCESS MESSAGE.
   *
   * Taking a parcel in cleared the form and refreshed the shelf, silently — the
   * desk's only evidence that anything happened was a list they were not
   * looking at. On a slow connection the button simply appeared to do nothing,
   * so it was pressed again, and two rows were logged for one parcel.
   *
   * `submitting` disables the control while the request is open, and the toast
   * names what was recorded rather than saying "Saved".
   */
  const submitIntake = async () => {
    if (!buildingId || !recipient.name.trim() || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await logPackage(buildingId, {
        recipient_name: recipient.name.trim(),
        unit_id: unitId,
        unit_label: unitLabel,
        carrier,
        tracking,
        shelf,
        // Sent ONLY when a human decided. Omitting them lets the server's
        // autocapture resolve it — and record 'single_match' rather than
        // 'picked', which would falsely claim a person confirmed it.
        ...(Object.keys(scanProvenance).length ? { field_provenance: scanProvenance } : {}),
        ...(recipient.resolution === 'unset'
          ? {}
          : {
              recipient_occupancy_id: recipient.occupancyId,
              recipient_resolution: recipient.resolution,
            }),
      });
      const loggedFor = recipient.name.trim();

      resetIntake();
      await refresh();
      toast.success(`Parcel logged for ${loggedFor}.`);
    } catch (e) {
      const body = (e as AxiosError<AmbiguityError>).response?.data;
      setError(body?.message || 'Could not log the parcel.');
      // The 400 carries the exact rows it refused over.
      if (body?.candidates?.length) setServerCandidates(body.candidates);
    } finally {
      setSubmitting(false);
    }
  };

  const submitRelease = async (pkg: Package) => {
    if (!collector.name.trim() || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await releasePackage(pkg.id, {
        released_to: collector.name.trim(),
        ...(collector.resolution === 'unset'
          ? {}
          : {
              released_to_occupancy_id: collector.occupancyId,
              released_to_resolution: collector.resolution,
            }),
      });
      const handedTo = collector.name.trim();

      setReleasing(null);
      setCollector(EMPTY_PERSON);
      await refresh();
      toast.success(`Parcel handed to ${handedTo}.`);
    } catch (e) {
      const body = (e as AxiosError<AmbiguityError>).response?.data;
      setError(body?.message || 'Could not release the parcel.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <NavbarAndContentContainer>
          <ContentAndButtonsContainer>
            <Body>
              {/* The desk arrives here from the Packages tile and had NO way
                  back short of the browser button. backTo is explicit, so a
                  refresh or a deep link still lands on the menu. */}
              <ScreenHeader
                title={
                  // NAME THE ACTION, NOT THE DOOR. "Packages — handover" and
                  // "Packages — intake" are labels for the same noun, and the
                  // owner reported (2026-09-07) that the two menu entries "show
                  // the same thing" — which, one word of heading aside, they
                  // did: both opened with the intake form and put the shelf
                  // below it. A concierge arriving to GIVE a parcel out was
                  // looking at the form for TAKING one in.
                  section === 'handover'
                    ? 'Hand a parcel over'
                    : section === 'intake'
                    ? 'Take a parcel in'
                    : 'Parcels'
                }
                subtitle={
                  selectedBuilding?.property_name || 'No building selected'
                }
              />

              {error && <Alert>{error}</Alert>}

              {/* ---------- INTAKE ---------- */}
              {/* WHICH HALF COMES FIRST IS THE WHOLE FIX.
                  This screen stays ONE screen — splitting it would duplicate
                  the shelf, the resident resolution and the release rules, and
                  the day the copies disagreed about `can_release` a parcel
                  would go to the wrong person. But the door you came through
                  decides what is under your eyes when you arrive.
                  `Body` is a flex column, so this reorders what is PAINTED
                  without moving a line of logic; the header keeps the default
                  order 0 and stays on top. */}
              <Card style={{ order: section === 'handover' ? 2 : 1 }}>
                <CardTitle>Log a parcel</CardTitle>
                {/* THREE EQUAL WAYS IN, not one with fallbacks. The label scan
                    is the fastest when there IS a readable label; the two
                    dropdown searches are what the desk uses when the courier
                    hands over a hand-written box, or when the concierge simply
                    asks "who is this for?" and types the answer. Neither is a
                    degraded mode. */}
                <Hint>
                  Scan the label, search the unit, or search the resident — any
                  one of them fills the rest.
                </Hint>
                <Grid>
                  <UnitSelect
                    value={unitLabel}
                    buildingId={buildingId}
                    unitId={unitId}
                    onChange={(label, id) => {
                      setUnitLabel(label);
                      setUnitId(id);
                      // A recipient resolved against the old unit is not the
                      // answer for the new one.
                      setRecipient(r => ({
                        ...r,
                        occupancyId: null,
                        resolution: 'unset',
                      }));
                      setServerCandidates([]);
                    }}
                  />
                  <PersonField
                    label="Recipient"
                    value={recipient.name}
                    onChange={next => setRecipient(r => ({ ...r, name: next }))}
                    // THE dropdown from the original request.
                    resolves
                    buildingId={buildingId}
                    unitId={unitId}
                    resolution={recipient.resolution}
                    occupancyId={recipient.occupancyId}
                    onResolve={picked =>
                      setRecipient(r => ({
                        ...r,
                        occupancyId: picked.occupancy_id,
                        resolution: picked.resolution,
                      }))
                    }
                    onRequirePickChange={handleRecipientPick}
                    serverCandidates={serverCandidates}
                    placeholder="Who is it for?"
                  />
                  <Field>
                    <Label htmlFor="parcel-carrier">Carrier</Label>
                    <Input
                      id="parcel-carrier"
                      value={carrier}
                      maxLength={64}
                      placeholder="DHL, UPS, Royal Mail…"
                      onChange={e => setCarrier(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="parcel-tracking">Tracking number</Label>
                    <Input
                      id="parcel-tracking"
                      value={tracking}
                      maxLength={64}
                      placeholder="Full tracking number"
                      onChange={e => setTracking(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="parcel-shelf">Shelf</Label>
                    <Input
                      id="parcel-shelf"
                      value={shelf}
                      maxLength={32}
                      placeholder="A3"
                      onChange={e => setShelf(e.target.value)}
                    />
                  </Field>
                </Grid>
                {/* NO "Scan recipient ID" HERE — REMOVED DELIBERATELY.
                    Intake is the moment the COURIER hands the box to the DESK.
                    The recipient is not standing there and has no document to
                    scan; the concierge is the one taking custody. The button
                    therefore invited a scan of the only ID actually present —
                    the concierge's own — and wrote it into the parcel's
                    recipient field with `resolution: 'unset'`, which is the
                    exact "wrong Maria" state this screen exists to prevent.
                    The two scans that ARE real remain: "Scan parcel label"
                    above (the label is on the box, at intake) and "Scan
                    collector ID" on the shelf below (the collector IS present,
                    at release). */}
                {/* THE SCAN SITS WITH THE SUBMIT, NOT ABOVE THE FORM.
                    At the top it read as step one of a wizard — as though the
                    form below could not be filled until the label had been
                    scanned. It is one of three equal ways to fill these fields,
                    and the desk reaches for it while looking at the box in its
                    hands, which is the same moment it is about to press Log
                    parcel. Directly above the submit is where that hand already
                    is. */}
                <ScanRow>
                  <ScanIdButton
                    mode="parcel"
                    buildingId={buildingId}
                    label="Scan parcel label"
                    disabled={!buildingId}
                    onResult={r => {
                      // The label usually carries the UNIT, which is what
                      // narrows the resident picker to that unit's occupants —
                      // and that is what resolves the recipient with no further
                      // interaction.
                      if (r.unit_label) setUnitLabel(r.unit_label);
                      if (r.unit_id) setUnitId(r.unit_id);
                      if (r.carrier) setCarrier(r.carrier);
                      if (r.tracking) setTracking(r.tracking);
                      setRecipient({
                        name: r.recipient_name || '',
                        occupancyId: null,
                        resolution: 'unset',
                        unitLabel: null,
                      });
                      setScanProvenance({
                        recipient_name: {
                          source: 'scan',
                          raw: r.raw.recipient_name || '',
                          confidence: r.confidence,
                        },
                      });
                      setServerCandidates([]);
                      if (!r.unit_matched && r.unit_label) {
                        setError(
                          `The label says "${r.unit_label}" — this building has no such unit. Pick the unit below.`
                        );
                      } else {
                        setError(null);
                      }
                    }}
                  />
                </ScanRow>
                <div style={{ marginTop: spacing.sm }}>
                  <NavButton
                    // Blocked while two residents share the name and nobody has
                    // chosen. The server refuses this save anyway; disabling is
                    // the courteous version of a rejection that is coming.
                    disabled={
                      !recipient.name.trim() ||
                      mustPick ||
                      !buildingId ||
                      // ...and while the previous press is still in flight, so a
                      // slow connection cannot log the same parcel twice.
                      submitting
                    }
                    direction="forward"
                    text={submitting ? 'Logging…' : 'Log parcel'}
                    onClick={submitIntake}
                  />
                </div>
              </Card>

              {/* ---------- THE SHELF (the handover door lands here) ------- */}
              <Card
                ref={handoverRef}
                style={{ order: section === 'handover' ? 1 : 2 }}
              >
                <CardTitle>On the shelf</CardTitle>
                {/* THE COUNTS, IN THE SHAPE THE OTHER OUT SCREENS USE. It was a
                    parenthesis in the heading — a number a desk had to read a
                    sentence to find — while the fact that matters most here,
                    how many of these cannot be handed over yet, was only
                    discoverable one row at a time. */}
                {packages.length > 0 && (
                  <ShelfSearchRow>
                    <ShelfSearchInput
                      type="search"
                      value={shelfQuery}
                      placeholder="Search the shelf by name, unit, carrier or shelf"
                      aria-label="Search the parcel shelf"
                      onChange={event => setShelfQuery(event.target.value)}
                    />
                  </ShelfSearchRow>
                )}
                <ShelfStats>
                  <ShelfStat tone={packages.length ? 'brand' : 'plain'}>
                    <b>{packages.length}</b>
                    <span>On the shelf</span>
                  </ShelfStat>
                  <ShelfStat tone={blockedCount ? 'warning' : 'plain'}>
                    <b>{blockedCount}</b>
                    <span>Cannot release</span>
                  </ShelfStat>
                  <ShelfStat>
                    <b>{packages.length - blockedCount}</b>
                    <span>Ready</span>
                  </ShelfStat>
                </ShelfStats>
                {/* Skeleton, empty shelf and the rows are fills of ONE box
                    that crossfade; the floor is three skeleton rows, so the
                    card does not collapse between them. A refresh never
                    swaps the rows for the skeleton — it dims them. */}
                <StateSwitch state={shelfState} minHeight={192}>
                  {shelfState === 'loading' ? (
                    <ListSkeleton rows={3} label="the parcel shelf" />
                  ) : shelfState === 'empty' ? (
                    <Empty>Nothing awaiting pickup.</Empty>
                  ) : (
                <Staleable stale={loading}>
                {visibleParcels.length === 0 && (
                  <Empty>
                    No parcel on the shelf matches “{shelfQuery.trim()}”. Clear
                    the search to see all {packages.length}.
                  </Empty>
                )}
                <Presence>
                {visibleParcels.map((p, index) => (
                  <ParcelItem
                    key={p.id}
                    {...listItemMotion(index)}
                    layout={visibleParcels.length <= LAYOUT_CAP}
                  >
                    <Row>
                      <ShelfLead>
                        {/* The RECIPIENT's initials. A shelf is scanned for a
                            name, so the name is what the mark carries. */}
                        <RowMark tone={p.can_release ? 'plain' : 'stale'} aria-hidden="true">
                          {initialsOf(p.recipient_name || '')}
                        </RowMark>
                        <RowMain>
                          <RowName>{p.recipient_name || '(no name)'}</RowName>
                          <RowMeta>
                            {/* HOW LONG IT HAS BEEN SITTING THERE.
                                `logged_at` was fetched on every row and printed
                                on none, so the shelf carried no date at all: a
                                parcel from this morning and one from three weeks
                                ago were the same row, on the screen whose job is
                                deciding which ones need chasing. */}
                            {[
                              waitingLabel(p.logged_at),
                              p.unit_label && `Unit ${p.unit_label}`,
                              p.carrier,
                              p.tracking_last4 && `••••${p.tracking_last4}`,
                              p.shelf && `Shelf ${p.shelf}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </RowMeta>
                        </RowMain>
                      </ShelfLead>
                      <Actions>
                        {/* The desk needs to see AT A GLANCE which parcels
                            cannot be handed over yet, not discover it by
                            pressing the button. */}
                        {p.recipient_resolution === 'unset' ? (
                          <Pill tone="warning">Recipient unconfirmed</Pill>
                        ) : p.recipient_resolution === 'no_match' ? (
                          <Pill tone="neutral">Not a resident</Pill>
                        ) : (
                          <Pill tone="success">Resident confirmed</Pill>
                        )}
                        <RowAction
                          type="button"
                          variant={releasing === p.id ? 'quiet' : 'primary'}
                          disabled={!p.can_release}
                          onClick={() => {
                            setReleasing(releasing === p.id ? null : p.id);
                            setCollector(EMPTY_PERSON);
                          }}
                        >
                          {releasing === p.id ? 'Cancel' : 'Release'}
                        </RowAction>
                      </Actions>
                    </Row>
                    {releasing === p.id && (
                      <Card style={{ marginTop: spacing.xs }}>
                        <CardTitle>Who collected it?</CardTitle>
                        {/* Resolved INDEPENDENTLY of the recipient. A spouse
                            collects, a neighbour collects — and if two
                            residents of the unit share a name, "who did you
                            hand it to" is exactly as ambiguous as "who is it
                            for". This is the answer that matters afterwards. */}
                        <PersonField
                          label="Released to"
                          value={collector.name}
                          onChange={next =>
                            setCollector(c => ({ ...c, name: next }))
                          }
                          resolves
                          buildingId={buildingId}
                          // The parcel's unit is still passed — but as a RANKING
                          // HINT, not a filter. Filtering to it is what made a
                          // neighbour collecting read as "Not a resident"; the
                          // release route matches building-wide for the same
                          // reason, so the picker and the save now agree.
                          unitId={p.unit_id}
                          unitScope="building"
                          resolution={collector.resolution}
                          occupancyId={collector.occupancyId}
                          onResolve={picked =>
                            setCollector(c => ({
                              ...c,
                              occupancyId: picked.occupancy_id,
                              resolution: picked.resolution,
                              unitLabel: picked.unit_label ?? null,
                            }))
                          }
                          onRequirePickChange={handleCollectorPick}
                          placeholder="Name of the person collecting"
                        />
                        {collector.unitLabel &&
                          p.unit_label &&
                          collector.unitLabel !== p.unit_label && (
                            <CrossUnit>
                              {`Collected by a resident of ${collector.unitLabel}; this parcel is addressed to ${p.unit_label}.`}
                            </CrossUnit>
                          )}
                        <ScanRow>
                          <ScanIdButton
                            mode="id"
                            buildingId={buildingId}
                            label="Scan collector ID"
                            onResult={r => {
                              // IT FILLS THE NAME AND STORES NOTHING. Owner
                              // decision: no document image, no extracted
                              // fields, and no provenance row for the scan
                              // itself — zero new PII retention. So this must
                              // NOT touch scanProvenance (that is the INTAKE's
                              // state) and the release body must stay free of
                              // field_provenance; ReleasePackageInput has no
                              // such member, and it should not gain one.
                              //
                              // The resolution is cleared with the name: who
                              // this refers to is an open question again, and
                              // autocapture must re-run against the new value
                              // rather than keep a link to the previous one.
                              setCollector({
                                name: `${r.first_name} ${r.last_name}`.trim(),
                                occupancyId: null,
                                resolution: 'unset',
                                unitLabel: null,
                              });
                            }}
                          />
                        </ScanRow>
                        <div style={{ marginTop: spacing.sm }}>
                          <RowAction
                            type="button"
                            variant="danger"
                            disabled={
                              !collector.name.trim() || collectorMustPick
                            }
                            onClick={() => submitRelease(p)}
                          >
                            Confirm handover
                          </RowAction>
                        </div>
                      </Card>
                    )}
                  </ParcelItem>
                ))}
                </Presence>
                </Staleable>
                  )}
                </StateSwitch>
              </Card>
            </Body>
          </ContentAndButtonsContainer>
    </NavbarAndContentContainer>
  );
};
